import {
  AccidentIssueWithCache,
  MutationUpdateAccidentIssueBulkArgs,
  UpdateAccidentIssueBulkResponse,
  UpdateAccidentIssueInput,
  UpdateAccidentIssueValuesInput,
} from '@/models/gen/graphql';
import { ConnectionOptions, GraphApiMethod } from '@/api/core';
import createGraphApiHook, { CreateGraphApiHookHook } from '@/hooks/createGraphApiHook';
import { getProperty, queryInput } from '@/utils';

import { TypedDocumentNode } from '@apollo/client';
import { UpdateAccidentIssue } from '@/features/AccidentHistory/api/queries/UpdateAccidentIssue';
import graphApi from '@/api';

// Service Function Signature
type ServiceMethodPayload = {
  [key in keyof AccidentIssueWithCache]?: any;
}[];
interface ServiceMethod {
  (payload?: ServiceMethodPayload, options?: ConnectionOptions): Promise<UpdateAccidentIssueBulkResponse>;
}

// SearchAccidentIssue Service
export const { updateAccidentIssue, useUpdateAccidentIssue } = ((): {
  updateAccidentIssue: ServiceMethod;
  useUpdateAccidentIssue: CreateGraphApiHookHook<ServiceMethod>;
} => {
  const [fetch] = graphApi(UpdateAccidentIssue).map(
    (fn: GraphApiMethod<TypedDocumentNode>): ServiceMethod =>
      async (
        payload?: Partial<UpdateAccidentIssueValuesInput & { id: string }>[],
        options?: ConnectionOptions
      ): Promise<UpdateAccidentIssueBulkResponse> => {
        const updates: UpdateAccidentIssueInput[] =
          payload
            ?.filter(({ id }) => id)
            ?.map(({ id, ...value }) => ({
              query: [
                {
                  id: queryInput(id),
                },
              ],
              value: {
                accidentDatetime: value?.accidentDatetime || null,
                additionalDriverScheduled: value?.additionalDriverScheduled ?? null,
                anyoneInjured: value?.anyoneInjured || null,
                canVehicleBeDriven: value?.canVehicleBeDriven || null,
                damageToOtherVehicles: value?.damageToOtherVehicles || null,
                description: value?.description || null,
                didDriverCallHelp: value?.didDriverCallHelp || null,
                dispatcherContactedManager: value?.dispatcherContactedManager ?? null,
                driverContactedManager: value?.driverContactedManager ?? null,
                infoOfCrewMembers: value?.infoOfCrewMembers || null,
                infoOfInjuredParties: value?.infoOfInjuredParties || null,
                medicalNeeded: value?.medicalNeeded || null,
                outboundFlightNumber: value?.outboundFlightNumber || null,
                outboundDatetime: value?.outboundDatetime || null,
                outboundFlightName: value?.outboundFlightName || null,
                dispatcherId: value?.dispatcherId || null,
                driverId: value?.driverId || null,
                safetyId: value?.safetyId || null,
                safetyNotified: value?.safetyNotified || null,
                samsaraCameraWorking: value?.samsaraCameraWorking || null,
                specificLocation: value?.specificLocation || null,
                stationText: value?.stationText || null,
                tripId: value?.tripId || null,
                typeOfMedicalAttention: value?.typeOfMedicalAttention || null,
                thirdPartyInformation: value?.thirdPartyInformation || null,
                wasCeoCalled: value?.wasCeoCalled || null,
                wasCrewInvolved: value?.wasCrewInvolved || null,
                wasDriverAtFault: value?.wasDriverAtFault || null,
                wasThirdPartyInvolved: value?.wasThirdPartyInvolved || null,
                vehicleId: value?.vehicleId || null,
              },
            })) ?? [];
        const args: MutationUpdateAccidentIssueBulkArgs = {
          input: {
            updates,
          },
        };
        const opts = { merge: options?.merge ? 'updateAccidentIssueBulk' : undefined };
        const res = await fn(args, opts);
        return getProperty('updateAccidentIssueBulk', res);
      }
  );
  const useService = createGraphApiHook(fetch);
  return {
    updateAccidentIssue: fetch,
    useUpdateAccidentIssue: useService,
  };
})();
